import * as React from 'react';
import { generateId } from '../../utils/DomUtil';
import { ButtonAttrs } from '../../utils/HtmlAttrs';
import { ProhibitPropsFrom } from '../../utils/ReactHelpers';
import { ListProps } from '../List/List';
import { DropdownButton, DropdownButtonProps } from './DropdownButton';

export type SelectDropdownProps<ValueType> = Omit<DropdownButtonProps, 'aria-controls' | 'aria-haspopup' | 'focusOnClose'> & {
  /** The content rendered in the dropdown button. The dropdown widget provides default behavior, this prop is only necessary for overrides. */
  children?: React.ReactNode;

  /** @default ```true``` */
  closeOnPopupBlur?: boolean;

  /** The List to render in the dropdown popup. */
  list: React.ReactElement<ProhibitPropsFrom<ListProps, 'onSelect' | 'onSelectAll' | 'selectOnEnterKey' | 'selectedValue' | 'selectedValues'>>;

  /** Invoked when the user wishes to select (or toggle selection of) a value in the dropdown. */
  onChange?: (value: string) => void;

  /**
   * Invoked when the user indicates they want to select all items. The action to take is left up to your usage context.
   *
   * In most cases, we suggest this event act as a toggle, i.e. it should select all items if all items are not currently selected,
   * but it should deselect all items if all items are already selected.
   *
   * The list's `onSelectAll` prop will be overridden to this prop.
   */
  onSelectAll?: (ValueType extends Set<unknown> ? ListProps['onSelectAll'] : never);

  /** The value(s) of the items which are currently selected. */
  value?: ValueType;
};

export type OverrideListProps = Required<Pick<ListProps, 'onSelect' | 'onSelectAll' | 'selectOnEnterKey' | 'selectedValue' | 'selectedValues'>>;

/** @private */
// tslint:disable-next-line:max-line-length
export abstract class SelectDropdown<ValueType> extends React.PureComponent<SelectDropdownProps<ValueType> & Omit<ButtonAttrs, keyof SelectDropdownProps<ValueType>>> {
  private readonly defaultLabelId: string = generateId();
  private labelId = () => this.props.labelId || this.defaultLabelId;
  private readonly defaultListId: string = generateId();
  private listId = () => this.props.list.props.id || this.defaultListId;
  private dropdownButtonRef: React.RefObject<DropdownButton> = React.createRef();

  /**
   * Repositions the popup, if it is currently shown. Might be useful if the popup contents weren't finished sizing at the time that the
   * popup was shown, e.g. if it contained an image which was still loading.
   */
  public repositionPopup(): void {
    this.dropdownButtonRef.current?.repositionPopup();
  }

  /**
   * Toggles the dropdown popup.
   * @param open optional parameter, ```true``` to open the dropdown or ```false``` to close it.
   */
  public toggle(open?: boolean): void {
    this.dropdownButtonRef.current?.toggle(open);
  }

  protected abstract makeListProps(): OverrideListProps;
  private renderList = (): React.ReactNode => {
    const list: React.ReactElement<ListProps> = this.props.list;
    const listAriaLabelledby = list.props['aria-labelledby'];

    return React.cloneElement(list, {
      'aria-labelledby': listAriaLabelledby === undefined ? this.labelId() : listAriaLabelledby,
      id: this.listId(),
      ...this.makeListProps() as unknown as ListProps,
    });
  }

  protected abstract renderButtonContent(): React.ReactNode;
  public render(): React.ReactElement {
    const { children, list, onChange, onSelectAll, value, ...dropdownProps } = this.props;

    return (
      <DropdownButton
        aria-controls={this.listId()}
        closeOnPopupBlur={true}
        labelId={this.labelId()}
        {...dropdownProps}
        aria-haspopup='listbox'
        focusOnClose={true}
        // tslint:disable-next-line:deprecation
        popupContent={dropdownProps.popupContent || dropdownProps.renderPopupContent?.() || this.renderList()}
        ref={this.dropdownButtonRef}
      >
        {this.renderButtonContent()}
      </DropdownButton>
    );
  }
}
